import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Camera, ReceiptText, Sparkles } from "lucide-react";

const UNITS = ["count", "g", "kg", "ml", "l", "pack", "cup", "tbsp", "tsp"];
const CATEGORIES = [
  { v: "grains_pulses", l: "Grains & Pulses" },
  { v: "vegetables", l: "Vegetables" },
  { v: "proteins", l: "Proteins" },
  { v: "dairy_fats", l: "Dairy & Fats" },
  { v: "condiments_pantry", l: "Condiments & Pantry" },
  { v: "fruits", l: "Fruits" },
  { v: "beverages", l: "Beverages" },
  { v: "other", l: "Other" },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.v, c.l]));

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", quantity: 0, initial_quantity: 0, unit: "count", category: "other", low_threshold: 0 });
  const [scanOpen, setScanOpen] = useState(false);
  const [scanMode, setScanMode] = useState("ingredient");
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    const { data } = await api.get("/inventory");
    setItems(data);
    try {
      const { data: h } = await api.get("/inventory/health");
      setHealth(h);
    } catch (_) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const reset = () => setForm({ name: "", quantity: 0, initial_quantity: 0, unit: "count", category: "other", low_threshold: 0 });

  const save = async () => {
    if (!form.name.trim()) return toast.warning("Name is required");
    const payload = { ...form };
    if (!payload.initial_quantity) payload.initial_quantity = payload.quantity;
    if (edit) {
      await api.patch(`/inventory/${edit.id}`, payload);
      toast.success("Updated");
    } else {
      await api.post("/inventory", payload);
      toast.success("Added");
    }
    setOpen(false); setEdit(null); reset(); load();
  };

  const openEdit = (it) => {
    setEdit(it);
    setForm({
      name: it.name,
      quantity: it.quantity,
      initial_quantity: it.initial_quantity || it.quantity,
      unit: it.unit,
      category: it.category || "other",
      low_threshold: it.low_threshold || 0,
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove from pantry?")) return;
    await api.delete(`/inventory/${id}`);
    load();
  };

  const lowItems = items.filter((i) => i.low_threshold > 0 && i.quantity <= i.low_threshold);

  const runScan = async (file) => {
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    try {
      const fd = new FormData();
      fd.append("mode", scanMode);
      fd.append("file", file);
      const token = localStorage.getItem("tuha_token");
      const res = await fetch(`${API}/vision/analyze`, {
        method: "POST", credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setScanResult(data.result);
    } catch (e) {
      toast.error("Scan failed: " + (e.message || ""));
    } finally {
      setScanning(false);
    }
  };

  const acceptScanItems = async () => {
    if (!scanResult?.items?.length) return;
    const items = scanResult.items.map((it) => ({
      name: it.name,
      quantity: Number(it.quantity) || 1,
      unit: it.unit || "count",
      category: it.category || "other",
      low_threshold: 0,
      notes: "added via image scan",
    }));
    await api.post("/inventory/bulk", { items });
    toast.success(`Added ${items.length} item${items.length > 1 ? "s" : ""}`);
    setScanOpen(false); setScanResult(null); load();
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 relative">
        <div className="absolute inset-0 z-30 flex items-start justify-center pt-20 px-6" data-testid="pantry-coming-soon">
          <div className="bg-surface-elevated border border-border rounded-3xl p-8 text-center max-w-md shadow-2xl">
            <div className="text-overline">pantry</div>
            <h2 className="font-serif-display text-4xl text-primary mt-1">Coming soon</h2>
            <p className="text-foreground/70 mt-3 text-sm">Smart pantry &amp; grocery management is being plated. We'll let you know the moment it's ready to serve.</p>
          </div>
        </div>
        <div className="blur-[6px] pointer-events-none select-none opacity-50" aria-hidden="true">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <div className="text-overline">pantry</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-primary tracking-tight">Your kitchen inventory</h1>
            <p className="text-foreground/70 mt-1">{items.length} item{items.length === 1 ? "" : "s"} on hand{lowItems.length ? ` • ${lowItems.length} running low` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={scanOpen} onOpenChange={(o) => { setScanOpen(o); if (!o) setScanResult(null); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="scan-btn"><Camera size={16} className="mr-1" /> Scan</Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl max-w-xl">
                <DialogHeader><DialogTitle className="font-serif-display text-2xl">Scan to pantry</DialogTitle></DialogHeader>
                <div className="flex gap-2 mb-2">
                  <Button variant={scanMode === "ingredient" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setScanMode("ingredient")} data-testid="scan-mode-ingredient">
                    <Camera size={14} className="mr-1" /> Ingredients
                  </Button>
                  <Button variant={scanMode === "receipt" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setScanMode("receipt")} data-testid="scan-mode-receipt">
                    <ReceiptText size={14} className="mr-1" /> Receipt
                  </Button>
                </div>
                <label className="block border-2 border-dashed border-border rounded-2xl p-8 bg-surface text-center cursor-pointer hover:bg-muted transition">
                  <Sparkles className="mx-auto text-secondary mb-2" size={22} />
                  <div className="text-sm font-medium">{scanning ? "Analyzing image…" : "Click to choose a JPEG/PNG/WEBP"}</div>
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => runScan(e.target.files?.[0])} data-testid="scan-file-input" />
                </label>
                {scanResult?.items?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-overline mb-2">Detected ({scanResult.items.length})</div>
                    <div className="max-h-64 overflow-y-auto scrollbar-thin divide-y divide-border" data-testid="scan-results">
                      {scanResult.items.map((it, i) => (
                        <div key={i} className="py-2 flex items-center justify-between text-sm">
                          <span className="font-medium">{it.name}</span>
                          <span className="text-muted-foreground">{it.quantity} {it.unit} {it.category ? `• ${it.category}` : ""}</span>
                        </div>
                      ))}
                    </div>
                    <Button className="w-full mt-3 rounded-full bg-primary" onClick={acceptScanItems} data-testid="scan-accept-btn">Add all to pantry</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEdit(null); reset(); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="add-inventory-btn"><Plus size={16} className="mr-1" /> Add item</Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl">
                <DialogHeader><DialogTitle className="font-serif-display text-2xl">{edit ? "Edit" : "Add"} pantry item</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" /></div>
                  <div><Label>Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} data-testid="inv-qty" /></div>
                  <div><Label>Unit</Label>
                    <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                      <SelectTrigger data-testid="inv-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="inv-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Low alert at</Label><Input type="number" value={form.low_threshold} onChange={(e) => setForm({ ...form, low_threshold: Number(e.target.value) })} data-testid="inv-low" /></div>
                  <div><Label>Initial / full quantity</Label><Input type="number" value={form.initial_quantity} onChange={(e) => setForm({ ...form, initial_quantity: Number(e.target.value) })} placeholder="for the fill meter" data-testid="inv-initial" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={save} className="rounded-full bg-primary" data-testid="save-inv-btn">{edit ? "Update" : "Add"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {items.length === 0 && (
          <>
            {health && <PantryHealthCard health={health} />}
            <div className="bg-surface-elevated border border-border rounded-3xl p-10 text-center">
              <div className="text-overline mb-2">empty pantry</div>
              <div className="font-serif-display text-2xl text-primary mb-2">Start by scanning your fridge or a receipt.</div>
              <div className="text-foreground/70">Or add items one-by-one with the button above.</div>
            </div>
          </>
        )}

        {items.length > 0 && (
          <>
            {health && <PantryHealthCard health={health} />}
            <div className="space-y-6" data-testid="inventory-list">
              {CATEGORIES.map(({ v: cat, l: label }) => {
                const inCat = items.filter((i) => (i.category || "other") === cat);
                if (inCat.length === 0) return null;
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-2.5 px-1">
                      <span className="text-overline">{label}</span>
                      <span className="text-xs text-muted-foreground">{inCat.length}</span>
                      <div className="flex-1 h-px bg-border/70" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                      {inCat.map((it) => {
                        const init = Number(it.initial_quantity) || Number(it.quantity) || 1;
                        const qty = Number(it.quantity) || 0;
                        const ratio = init > 0 ? Math.max(0, Math.min(1, qty / init)) : 0;
                        const pct = Math.round(ratio * 100);
                        const low = (it.low_threshold > 0 && qty <= it.low_threshold) || ratio <= 0.2;
                        const critical = qty <= 0;
                        const fillColor = critical ? "bg-destructive" : (low ? "bg-secondary" : "bg-primary");
                        const dotColor = critical ? "bg-destructive" : (low ? "bg-secondary" : "bg-primary");
                        return (
                          <div key={it.id} className="group rounded-2xl border border-border bg-surface px-3.5 py-3 hover:border-primary/40 transition-colors" data-testid={`inv-row-${it.id}`}>
                            <div className="flex items-center justify-between gap-1.5 mb-2">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className={`h-2 w-2 rounded-full shrink-0 ${dotColor}`} />
                                <span className="font-medium text-sm text-primary truncate" title={it.notes ? `${it.name} — ${it.notes}` : it.name}>{it.name}</span>
                              </div>
                              <div className="flex shrink-0 -mr-1.5">
                                <button onClick={() => openEdit(it)} className="p-1 text-muted-foreground hover:text-primary transition-colors" data-testid={`edit-${it.id}`} aria-label="Edit"><Pencil size={13} /></button>
                                <button onClick={() => remove(it.id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors" data-testid={`del-inv-${it.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                              </div>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className={`h-full ${fillColor} transition-all`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1.5 flex items-center justify-between" data-testid={`fill-${it.id}`}>
                              <span>{qty} / {init} {it.unit}</span>
                              {critical ? <span className="text-destructive font-medium">empty</span> : (low ? <span className="text-secondary font-medium">low</span> : null)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        </div>
      </div>
    </Layout>
  );
}

function PantryHealthCard({ health }) {
  const score = health.score || 0;
  const band = score >= 75 ? "Healthy" : score >= 50 ? "Decent" : score >= 25 ? "Needs work" : "Critical";
  const ringColor = score >= 75 ? "stroke-primary" : score >= 50 ? "stroke-accent" : score >= 25 ? "stroke-secondary" : "stroke-destructive";
  const r = 36;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - score / 100);

  return (
    <div className="bg-surface-elevated border border-border rounded-3xl p-5 mb-6 grid md:grid-cols-[auto_1fr_1fr] gap-6 items-center" data-testid="pantry-health-card">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
          <circle cx="50" cy="50" r={r} className="stroke-muted" strokeWidth="8" fill="transparent" />
          <circle
            cx="50" cy="50" r={r} className={`${ringColor} transition-all duration-500`}
            strokeWidth="8" fill="transparent" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={offset}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-serif-display text-2xl text-primary leading-none">{score}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">score</div>
        </div>
      </div>
      <div>
        <div className="text-overline">pantry health</div>
        <div className="font-serif-display text-2xl text-primary">{band}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {health.total_items} items across {Object.values(health.categories || {}).filter((n) => n > 0).length} categories
        </div>
        {health.missing_healthy_categories?.length > 0 && (
          <div className="text-xs text-foreground/70 mt-2">
            Missing: <span className="font-medium">{health.missing_healthy_categories.map((c) => c.replace("_", " & ")).join(", ")}</span>
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        {health.critical_items?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <span className="text-overline w-full">empty / critical</span>
            {health.critical_items.slice(0, 6).map((n, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">{n}</span>
            ))}
          </div>
        )}
        {health.low_items?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <span className="text-overline w-full">running low</span>
            {health.low_items.slice(0, 8).map((n, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-secondary/20 text-secondary">{n}</span>
            ))}
          </div>
        )}
        {(!health.critical_items?.length && !health.low_items?.length) && (
          <div className="text-xs text-muted-foreground">All items are well-stocked.</div>
        )}
      </div>
    </div>
  );
}
